import {
	MobileNav,
	MobileNavHeader,
	MobileNavMenu,
	MobileNavToggle,
	NavBody,
	Navbar,
	NavbarButton,
	NavbarLogo,
	NavItems,
} from "@designs-by-maryam/ui/components/resizable-navbar";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import UserMenu from "@/components/auth/user-menu";
import { HEADER_CONTENT, type NavItem } from "@/data/header-content";
import { authClient } from "@/lib/auth-client";
import { siteSettingsQueryOptions } from "@/lib/query-options";
import { orpc } from "@/utils/orpc";
import { LanguageToggle } from "./language-toggle";
import { ThemeToggle } from "./theme-toggle";

export default function Header() {
	const { data: session } = authClient.useSession();
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const navigate = useNavigate();
	const { location } = useRouterState();

	// Feature flags for experimental features
	const { data: featureFlags } = useQuery(orpc.getFeatureFlags.queryOptions());

	// Fetch header navigation from CMS
	const { data: cmsSettings } = useQuery({
		...siteSettingsQueryOptions("headerNavigation"),
		staleTime: 60 * 60 * 1000, // 1 hour - navigation rarely changes
	});

	// Use CMS nav items if available, otherwise fallback to static
	const publicNavItems = useMemo(() => {
		if (cmsSettings?.value) {
			const navItems = cmsSettings.value as unknown as NavItem[];
			if (Array.isArray(navItems) && navItems.length > 0) {
				return navItems;
			}
		}
		return HEADER_CONTENT.navigation;
	}, [cmsSettings]);

	// Handle nav click with TanStack Router
	const handleNavClick = (link: string) => {
		navigate({ to: link });
		setMobileMenuOpen(false);
	};

	return (
		<Navbar className="top-0">
			{/* Desktop Navigation */}
			<NavBody className="bg-background/80">
				{/* Logo */}
				<NavbarLogo />

				{/* Nav Items - Center */}
				<NavItems
					items={publicNavItems}
					onItemClick={() => setMobileMenuOpen(false)}
				/>

				{/* Right Side */}
				<div className="relative z-20 flex items-center gap-3">
					<ThemeToggle />
					{featureFlags?.enableLanguageToggle && <LanguageToggle />}
					{session?.user ? (
						<UserMenu />
					) : (
						<NavbarButton
							as="button"
							onClick={() => navigate({ to: "/contact" })}
							variant="gradient"
						>
							Get In Touch
						</NavbarButton>
					)}
				</div>
			</NavBody>

			{/* Mobile Navigation */}
			<MobileNav className="bg-background/80">
				<MobileNavHeader>
					<Link
						className="flex items-center"
						onClick={() => setMobileMenuOpen(false)}
						to="/"
					>
						<span className="font-brand font-semibold text-foreground tracking-wide">
							DESIGNS{" "}
							<span className="font-normal italic text-muted-foreground">
								by
							</span>{" "}
							MARYAM
						</span>
					</Link>
					<MobileNavToggle
						isOpen={mobileMenuOpen}
						onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
					/>
				</MobileNavHeader>

				<MobileNavMenu
					isOpen={mobileMenuOpen}
					onClose={() => setMobileMenuOpen(false)}
				>
					{publicNavItems.map((item) => {
						const isActive = location.pathname === item.link;
						return (
							<button
								aria-current={isActive ? "page" : undefined}
								className={`w-full min-h-[44px] flex items-center px-3 py-2 rounded-lg text-left transition-colors ${
									isActive
										? "bg-primary/10 text-foreground font-medium"
										: "text-muted-foreground hover:bg-muted hover:text-foreground"
								}`}
								key={item.link}
								onClick={() => handleNavClick(item.link)}
								type="button"
							>
								{item.name}
							</button>
						);
					})}
					<div className="mt-4 flex items-center gap-2 border-border border-t pt-4">
						<ThemeToggle />
						{featureFlags?.enableLanguageToggle && <LanguageToggle />}
					</div>
					<div className="mt-4">
						{session?.user ? (
							<div className="flex items-center gap-2">
								<span className="text-muted-foreground text-sm">
									{session.user.email}
								</span>
							</div>
						) : (
							<NavbarButton
								as="button"
								className="w-full"
								onClick={() => {
									navigate({ to: "/contact" });
									setMobileMenuOpen(false);
								}}
								variant="gradient"
							>
								Get In Touch
							</NavbarButton>
						)}
					</div>
				</MobileNavMenu>
			</MobileNav>
		</Navbar>
	);
}
